import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { ChevronsUpDown, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIntegrationService } from '@/hooks/use-integration-service';
import type { CostCentre } from '@/types/financials';

// Maximum length for cost centre codes (matches backend MaxLength(100))
const MAX_COST_CENTRE_CODE_LENGTH = 100;

interface CostCentrePickerProps {
  readonly value?: string;
  readonly displayValue?: string;
  readonly onValueChange: (code: string, name: string) => void;
  readonly projectCode?: string;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly className?: string;
  readonly costCentres?: CostCentre[];
  readonly loading?: boolean;
  readonly error?: boolean;
}

export function CostCentrePicker({
  value,
  displayValue,
  onValueChange,
  projectCode,
  disabled = false,
  placeholder = 'Select or enter cost centre...',
  className,
  costCentres: externalCostCentres,
  loading: externalLoading,
  error: externalError,
}: Readonly<CostCentrePickerProps>) {
  const { service, isEnabled } = useIntegrationService();
  const [internalCostCentres, setInternalCostCentres] = useState<CostCentre[]>([]);
  const [filteredCostCentres, setFilteredCostCentres] = useState<CostCentre[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalError, setInternalError] = useState(false);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? '');

  const useExternalData = externalCostCentres !== undefined;
  const costCentres = useExternalData ? externalCostCentres : internalCostCentres;
  const loading = useExternalData ? (externalLoading ?? false) : internalLoading;
  const error = useExternalData ? (externalError ?? false) : internalError;

  // Sync input value with external value changes
  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  useEffect(() => {
    if (useExternalData) return;

    // Only fetch cost centres if integration is enabled
    if (!isEnabled) {
      setInternalCostCentres([]);
      return;
    }

    const fetchCostCentres = async () => {
      setInternalLoading(true);
      setInternalError(false);
      try {
        const data = await service.getCostCentres();
        setInternalCostCentres(data);
      } catch (err) {
        console.error('Failed to fetch cost centres:', err);
        setInternalError(true);
      } finally {
        setInternalLoading(false);
      }
    };

    fetchCostCentres();
  }, [useExternalData, service, isEnabled]);

  useEffect(() => {
    if (projectCode) {
      const filtered = costCentres.filter(cc => cc.projectCode === projectCode);
      setFilteredCostCentres(filtered);
    } else {
      setFilteredCostCentres(costCentres);
    }
  }, [costCentres, projectCode]);

  const hasIntegrationData = filteredCostCentres.length > 0;

  const handleSelect = (costCentre: CostCentre) => {
    onValueChange(costCentre.code, costCentre.name);
    setInputValue(costCentre.code);
    setOpen(false);
  };

  const handleClear = () => {
    onValueChange('', '');
    setInputValue('');
    setOpen(false);
  };

  const handleInputChange = (newValue: string) => {
    // Enforce max length
    const truncatedValue = newValue.slice(0, MAX_COST_CENTRE_CODE_LENGTH);
    setInputValue(truncatedValue);
    
    // Check if the value matches an existing cost centre
    const matchingCostCentre = costCentres.find(cc => cc.code === truncatedValue);
    if (matchingCostCentre) {
      onValueChange(matchingCostCentre.code, matchingCostCentre.name);
    } else {
      // Custom entry - use the value as both code and name
      onValueChange(truncatedValue, truncatedValue);
    }
  };

  const handleInputBlur = () => {
    // Ensure the value is committed on blur
    if (inputValue !== value) {
      const matchingCostCentre = costCentres.find(cc => cc.code === inputValue);
      if (matchingCostCentre) {
        onValueChange(matchingCostCentre.code, matchingCostCentre.name);
      } else {
        onValueChange(inputValue, inputValue);
      }
    }
  };

  const getSelectedDisplay = () => {
    if (loading) {
      return (
        <span className="flex items-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading...
        </span>
      );
    }
    if (value) {
      const selectedCostCentre = costCentres.find(cc => cc.code === value);
      if (selectedCostCentre) return `${selectedCostCentre.code} - ${selectedCostCentre.name}`;
      if (displayValue && displayValue !== value) return `${value} - ${displayValue}`;
      return value;
    }
    return placeholder;
  };

  if (disabled) {
    return (
      <Input
        value={displayValue && displayValue !== value ? `${value} - ${displayValue}` : (value ?? '')}
        readOnly
        className={cn('bg-muted', className)}
      />
    );
  }

  // If no integration data, show a simple input field for custom entry
  if (!hasIntegrationData && !loading) {
    return (
      <div className={className}>
        <Input
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          maxLength={MAX_COST_CENTRE_CODE_LENGTH}
          className="w-full"
        />
        {error && (
          <p className="text-sm text-destructive mt-1">Failed to load cost centres</p>
        )}
      </div>
    );
  }

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            aria-haspopup="listbox"
            aria-expanded={open}
            className={cn("w-full justify-between font-normal bg-white", className)}
            disabled={loading}
          >
            {getSelectedDisplay()}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput 
              placeholder="Search or enter custom value..." 
              value={inputValue}
              onValueChange={(search) => {
                setInputValue(search);
              }}
            />
            <CommandEmpty>
              {error && 'Failed to load cost centres.'}
              {!error && inputValue && (
                <CommandItem
                  onSelect={() => {
                    const truncatedValue = inputValue.slice(0, MAX_COST_CENTRE_CODE_LENGTH);
                    onValueChange(truncatedValue, truncatedValue);
                    setOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  Use &quot;{inputValue.slice(0, MAX_COST_CENTRE_CODE_LENGTH)}&quot;
                </CommandItem>
              )}
              {!error && !inputValue && 'Type to enter a custom value'}
            </CommandEmpty>
            <CommandGroup className="max-h-64 overflow-y-auto">
              <CommandItem
                key="none"
                value="none"
                onSelect={handleClear}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    !value ? "opacity-100" : "opacity-0"
                  )}
                />
                <span className="text-muted-foreground">None</span>
              </CommandItem>
              {filteredCostCentres.map((costCentre) => {
                const uniqueKey = costCentre.projectCode 
                  ? `${costCentre.code}-${costCentre.projectCode}` 
                  : costCentre.code;
                return (
                  <CommandItem
                    key={uniqueKey}
                    value={`${costCentre.code} ${costCentre.name} ${costCentre.projectCode ?? ''}`}
                    onSelect={() => handleSelect(costCentre)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === costCentre.code ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="font-medium">{costCentre.code}</span>
                    <span className="ml-2 text-muted-foreground">{costCentre.name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
      {error && (
        <p className="text-sm text-destructive mt-1">Failed to load cost centres</p>
      )}
    </div>
  );
}
